import { AnyAction } from 'redux';
import { Produktlinie } from '@ps20-frontend/frontend-models';
import { Injectable } from '@angular/core';
import { TARIF_CHECKED } from '@ps20-frontend/shop-integration-redux';
import { Selbstbeteiligung } from '@ps20/bff-models';
import { AnzahlTiereEnum, Stockmass } from './reducers/versicherungsnehmer.reducer';
import { AntragsummerGenerated } from './actiontypes/antragsummerGenerated';

export interface ThhPferdAction<T = undefined> extends AnyAction {
  payload?: T;
}

export interface StockmassMehrerePferdeChangedPayload {
  stockmass: Stockmass;
  index: number;
}

@Injectable()
export class ThhPferdActions {
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'THH_PFERD_ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'THH_PFERD_ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly PLZ_CHANGED = 'THH_PFERD_PLZ_CHANGED';
  static readonly ORT_CHANGED = 'THH_PFERD_ORT_CHANGED';
  static readonly STRASSE_CHANGED = 'THH_PFERD_STRASSE_CHANGED';
  static readonly HAUSNUMMER_CHANGED = 'THH_PFERD_HAUSNUMMER_CHANGED';
  static readonly GEBURTSDATUM_CHANGED = 'THH_PFERD_GEBURTSDATUM_CHANGED';
  static readonly QUOTEDB_REQUESTED = 'THH_PFERD_QUOTEDB_REQUESTED';
  static readonly PREISE_REQUESTED = 'THH_PFERD_PREISE_REQUESTED';
  static readonly ZUM_ANGEBOT_BUTTON_CLICKED = 'THH_PFERD_ZUM_ANGEBOT_BUTTON_CLICKED';
  static readonly ANZAHL_TIERE_CHANGED = 'THH_PFERD_ANZAHL_TIERE_CHANGED';
  static readonly STOCKMASS_CHANGED = 'STOCKMASS_CHANGED';
  static readonly VORSCHAEDEN_VORHANDEN = 'THH_PFERD_VORSCHAEDEN_VORHANDEN';
  static readonly VORSCHADEN_VALUE_CHANGED = 'THH_PFERD_VORSCHADEN_VALUE_CHANGED';
  static readonly ANZAHL_MEHRERE_PFERDE_CHANGED = 'THH_PFERD_ANZAHL_MEHRERE_PFERDE_CHANGED';
  static readonly STOCKMASS_MEHRERE_PFERDE_CHANGED = 'THH_PFERD_STOCKMASS_MEHRERE_PFERDE_CHANGED';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'THH_PFERD_SELBSTBETEILIGUNG_CHANGED';
  static readonly BRUTTOPREISE_CALCULATED = 'BRUTTOPREISE_CALCULATED';
  static readonly PRODUKTLINIEN_CHANGED = 'PRODUKTLINIEN_CHANGED';
  static readonly ABSPRUNG_INS_DASHBOARD = 'ABSPRUNG_INS_DASHBOARD';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly VERSICHERUNGSSUMME_CHANGED = 'VERSICHERUNGSSUMME_CHANGED';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly VORBELEGUNG_FINISHED = 'THH_PFERD_VORBELEGUNG_FINISHED';
  static readonly HANDLE_THH_PFERD_NAVIGATION_TO_ANGEBOT = 'HANDLE_THH_PFERD_NAVIGATION_TO_ANGEBOT';

  zurueckZumShopModalOpen(): ThhPferdAction {
    return { type: ThhPferdActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  zurueckZumShopModalClose(): ThhPferdAction {
    return { type: ThhPferdActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  plzChanged(changedPlz: string): ThhPferdAction<string> {
    return { type: ThhPferdActions.PLZ_CHANGED, payload: changedPlz };
  }

  ortChanged(changedOrt: string): ThhPferdAction<string> {
    return { type: ThhPferdActions.ORT_CHANGED, payload: changedOrt };
  }

  strasseChanged(changedStrasse: string): ThhPferdAction<string> {
    return { type: ThhPferdActions.STRASSE_CHANGED, payload: changedStrasse };
  }

  hausnummerChanged(changedHausnummer: string): ThhPferdAction<string> {
    return {
      type: ThhPferdActions.HAUSNUMMER_CHANGED,
      payload: changedHausnummer,
    };
  }

  geburtsdatumChanged(geburtsdatum: string): ThhPferdAction<string> {
    return {
      type: ThhPferdActions.GEBURTSDATUM_CHANGED,
      payload: geburtsdatum,
    };
  }

  quoteDBRequested(): ThhPferdAction {
    return { type: ThhPferdActions.QUOTEDB_REQUESTED };
  }

  preiseRequested(): ThhPferdAction {
    return { type: ThhPferdActions.PREISE_REQUESTED };
  }

  zumAngebotButtonClicked(): ThhPferdAction {
    return { type: ThhPferdActions.ZUM_ANGEBOT_BUTTON_CLICKED };
  }

  anzahlTiereChanged(anzahlTiere: AnzahlTiereEnum): ThhPferdAction<AnzahlTiereEnum> {
    return { type: ThhPferdActions.ANZAHL_TIERE_CHANGED, payload: anzahlTiere };
  }

  stockmassChanged(stockmass: Stockmass): ThhPferdAction<Stockmass> {
    return { type: ThhPferdActions.STOCKMASS_CHANGED, payload: stockmass };
  }

  vorschaedenVorhandenChanged(vorhanden: boolean): ThhPferdAction<boolean> {
    return { type: ThhPferdActions.VORSCHAEDEN_VORHANDEN, payload: vorhanden };
  }

  vorschadenValueChanged(
    vorschadenId: string,
    value: number,
  ): ThhPferdAction<{ vorschadenId: string; value: number }> {
    return {
      type: ThhPferdActions.VORSCHADEN_VALUE_CHANGED,
      payload: { vorschadenId, value },
    };
  }

  anzahlMehrerePferdeChanged(anzahlMehrerePferde: number): ThhPferdAction<number> {
    return {
      type: ThhPferdActions.ANZAHL_MEHRERE_PFERDE_CHANGED,
      payload: anzahlMehrerePferde,
    };
  }

  stockmassMehrerePferdeChanged(
    stockmass: Stockmass,
    index: number,
  ): ThhPferdAction<StockmassMehrerePferdeChangedPayload> {
    return {
      type: ThhPferdActions.STOCKMASS_MEHRERE_PFERDE_CHANGED,
      payload: { stockmass, index },
    };
  }

  selbstbeteiligungChanged(
    selbstbeteiligung: Selbstbeteiligung,
  ): ThhPferdAction<Selbstbeteiligung> {
    return {
      type: ThhPferdActions.SELBSTBETEILIGUNG_CHANGED,
      payload: selbstbeteiligung,
    };
  }

  bruttopreiseCalculated(
    produktlinienMitBruttopreisen: Array<Produktlinie>,
  ): ThhPferdAction<Array<Produktlinie>> {
    return {
      type: ThhPferdActions.BRUTTOPREISE_CALCULATED,
      payload: produktlinienMitBruttopreisen,
    };
  }

  tarifChecked(index: number): ThhPferdAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): ThhPferdAction<Produktlinie[]> {
    return {
      type: ThhPferdActions.PRODUKTLINIEN_CHANGED,
      payload: produktlinien,
    };
  }

  absprungInsDashboard(): ThhPferdAction {
    return { type: ThhPferdActions.ABSPRUNG_INS_DASHBOARD };
  }

  vorschlagdatenChanged(deferTarifierung = false): AnyAction {
    return deferTarifierung
      ? { type: ThhPferdActions.VORSCHLAGDATEN_CHANGED, payload: { deferTarifierung } }
      : { type: ThhPferdActions.VORSCHLAGDATEN_CHANGED };
  }

  versicherungsSummeChanged(
    versSummeLeistung: number,
  ): ThhPferdAction<{ versSummeLeistung: number }> {
    return {
      type: ThhPferdActions.VERSICHERUNGSSUMME_CHANGED,
      payload: { versSummeLeistung },
    };
  }

  antragsnummerGenerated(antragsnummer: string): ThhPferdAction<AntragsummerGenerated> {
    return {
      type: ThhPferdActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer },
    };
  }

  handleThhPferdNavigationToAngebot(): ThhPferdAction {
    return { type: ThhPferdActions.HANDLE_THH_PFERD_NAVIGATION_TO_ANGEBOT };
  }
}
